import type LocalForage from "localforage";

import store from "shared/services/localForageInstance";

const MAX_EXPIRING_ENTRIES = 200;

let pruneKeysTimeout: ReturnType<typeof setTimeout> | null = null;
let hasLoggedQuotaError = false;
let hasLoggedStorageError = false;

function isQuotaExceededError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "QuotaExceededError" ||
      error.name === "NS_ERROR_DOM_QUOTA_REACHED")
  );
}

function isStorageUnavailableError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "UnknownError" || error.name === "InvalidStateError")
  );
}

function handleQuotaError(): void {
  if (!hasLoggedQuotaError) {
    hasLoggedQuotaError = true;

    // eslint-disable-next-line no-console
    console.warn(
      "[StorageService] Storage quota exceeded. Writes will be skipped until space is available."
    );
  }
}

function handleStorageUnavailableError(): void {
  if (!hasLoggedStorageError) {
    hasLoggedStorageError = true;

    // eslint-disable-next-line no-console
    console.warn(
      "[StorageService] IndexedDB unavailable. Reads will fall back to sessionStorage; writes and maintenance operations will be skipped."
    );
  }
}

const StorageService = {
  debouncedPruneKeys() {
    if (pruneKeysTimeout !== null) {
      clearTimeout(pruneKeysTimeout);
    }

    pruneKeysTimeout = setTimeout(() => this.pruneKeys(), 1000);
  },
  async expireKeys() {
    try {
      const removalPromises: Array<void | Promise<void>> = [];

      await store.iterate((value: any, key: string) => {
        try {
          const expiry = JSON.parse(value)?.expireAfter;

          if (expiry && expiry < Date.now()) {
            removalPromises.push(this.remove(key, store));
          }
        } catch {
          // do nothing
        }
      });

      return await Promise.allSettled(removalPromises);
    } catch (error) {
      if (isStorageUnavailableError(error)) {
        handleStorageUnavailableError();

        return [];
      }

      throw error;
    }
  },
  async get(key: string, strategy = store): Promise<string | null> {
    try {
      return await Promise.resolve(
        sessionStorage.getItem(key) || (await strategy.getItem(key))
      );
    } catch (error) {
      if (isQuotaExceededError(error)) {
        handleQuotaError();

        return sessionStorage.getItem(key);
      }

      if (isStorageUnavailableError(error)) {
        handleStorageUnavailableError();

        return sessionStorage.getItem(key);
      }

      throw error;
    }
  },
  async pruneKeys() {
    await this.expireKeys();

    try {
      const entries: Array<{ expiry: number; key: string }> = [];

      await store.iterate((value: any, key: string) => {
        try {
          const expiry: number = JSON.parse(value)?.expireAfter;

          if (expiry) entries.push({ expiry, key });
        } catch {
          // do nothing
        }
      });

      if (entries.length <= MAX_EXPIRING_ENTRIES) return;

      const newestEntries = entries.sort(
        (entryA, entryB) => entryB.expiry - entryA.expiry
      );

      const oldestEntries = newestEntries.slice(MAX_EXPIRING_ENTRIES);

      await Promise.allSettled(
        oldestEntries.map((entry) => this.remove(entry.key, store))
      );
    } catch (error) {
      if (isStorageUnavailableError(error)) {
        handleStorageUnavailableError();

        return;
      }

      throw error;
    }
  },
  remove(key: string, strategy: LocalForage | Storage = store) {
    return strategy.removeItem(key);
  },
  async set(key: string, value: any, strategy: LocalForage | Storage = store) {
    try {
      return await strategy.setItem(key, value);
    } catch (error) {
      if (isQuotaExceededError(error)) {
        handleQuotaError();

        return undefined;
      }

      if (isStorageUnavailableError(error)) {
        handleStorageUnavailableError();

        return undefined;
      }

      throw error;
    }
  },
  setEx(key: string, value: any, ttl: number) {
    const data = {
      expireAfter: Date.now() + ttl,
      value,
    };

    return this.set(key, JSON.stringify(data), store);
  },
};

export default StorageService;
